const STORAGE_KEY = "readzh_attribution";

// Google 広告のクリック識別子の有効期間に合わせる
const RETENTION_DAYS = 90;
const RETENTION_MS = RETENTION_DAYS * 24 * 60 * 60 * 1000;

/**
 * 広告のクリック識別子
 *
 * backend の conversion.py の Attribution と 1 対 1 で対応する。
 * そのまま Data Manager API の adIdentifiers として送られるので、
 * フィールドを増減するときは backend と同時に変えること。
 */
export interface Attribution {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
}

const CLICK_ID_PARAMS: (keyof Attribution)[] = ["gclid", "gbraid", "wbraid"];

interface StoredAttribution {
  attribution: Attribution;
  capturedAt: number;
}

/**
 * 着地ページの URL からクリック識別子を取り出す
 */
const readFromUrl = (): Attribution => {
  const params = new URLSearchParams(window.location.search);

  const attribution: Attribution = {};
  for (const name of CLICK_ID_PARAMS) {
    const value = params.get(name);
    if (value) {
      attribution[name] = value;
    }
  }

  return attribution;
};

/**
 * 現在の URL にクリック識別子があれば保存する
 *
 * 別のクリックで来たときは、識別子が混ざらないように丸ごと置き換える（最後のクリックが残る）。
 * アプリの起動時に 1 回だけ呼ぶ。
 */
export const captureAttributionFromUrl = (): void => {
  const attribution = readFromUrl();
  if (Object.keys(attribution).length === 0) {
    return;
  }

  const stored: StoredAttribution = {
    attribution,
    capturedAt: Date.now(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    console.error("Failed to store attribution:", error);
  }
};

/**
 * 保存済みのクリック識別子を返す（無い / 期限切れなら空）
 */
export const getAttribution = (): Attribution => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to read attribution:", error);
    return {};
  }

  if (!raw) {
    return {};
  }

  let stored: StoredAttribution;
  try {
    stored = JSON.parse(raw) as StoredAttribution;
  } catch (error) {
    console.error("Stored attribution is broken:", error);
    return {};
  }

  if (
    !stored.attribution ||
    typeof stored.capturedAt !== "number" ||
    Date.now() - stored.capturedAt > RETENTION_MS
  ) {
    return {};
  }

  return stored.attribution;
};
